import { z } from "zod";

/**
 * Shared zod schemas for the auth flows. The same email + password
 * shape underpins login, signup, and reset, so co-locating keeps
 * validation messages consistent across the four routes.
 *
 * Email policy: just `.email()` for now. Cognito allows everything
 * the RFC does, so adding our own restrictions here would create a
 * "valid in form / rejected by Cognito" trap or vice versa.
 *
 * Password policy mirrors the Cognito User Pool requirements:
 *  - min 8 chars
 *  - at least one digit, one lowercase, one uppercase
 * Cognito additionally requires one symbol when configured; the
 * pool config in `amplify/auth/resource.ts` is the source of truth
 * — keep this schema in lockstep.
 */

export const emailSchema = z
  .string()
  .min(1, "Email is required")
  .email("Enter a valid email address");

export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[a-z]/, "Password must contain a lowercase letter")
  .regex(/[A-Z]/, "Password must contain an uppercase letter")
  .regex(/\d/, "Password must contain a digit");

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
  // Stored locally only — surfaces on the login form, never sent
  // to the BFF.
  remember: z.boolean(),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  email: emailSchema,
  code: z
    .string()
    .min(6, "Verification code is 6 characters")
    .max(6, "Verification code is 6 characters"),
  password: passwordSchema,
});

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const signupCredentialsSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    passwordConfirm: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    path: ["passwordConfirm"],
    message: "Passwords don't match",
  });

export type SignupCredentialsInput = z.infer<typeof signupCredentialsSchema>;

export const signupAttributesSchema = z.object({
  given_name: z.string().min(1, "First name is required"),
  family_name: z.string().min(1, "Last name is required"),
  company: z.string().min(1, "Company name is required"),
  // Clickwrap gate. The user must tick the legal-acceptance box before
  // the account is finalised. The accepted docs are recorded server-
  // side (cpf-acceptances) after login; this boolean only gates the
  // form and is NOT sent to Cognito (stripped in SignupView before the
  // update-attributes call).
  acceptedLegal: z.boolean().refine((v) => v === true, {
    message: "Please accept the agreements to continue",
  }),
});

export type SignupAttributesInput = z.infer<typeof signupAttributesSchema>;
